import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

interface AvatarWithInitialsProps {
  name: string
  src?: string | null
  alt?: string
  className?: string
  fallbackClassName?: string
  fallbackBgColor?: string
}

/**
 * Generates initials from a name
 * Takes the first letter of each word (up to 2 words)
 *
 * @example
 * getInitials("John Doe") // "JD"
 * getInitials("Jane") // "JA"
 * getInitials("Mary Ann Smith") // "MS"
 */
export function getInitials(name: string): string {
  if (!name || name.trim() === '') return 'U'

  return name
    .trim()
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Avatar with Initials Component
 *
 * Displays an avatar with an image or falls back to initials
 * Automatically generates initials from the provided name
 *
 * @param name - Full name to generate initials from
 * @param src - Optional image URL
 * @param alt - Optional alt text (defaults to name)
 * @param className - Optional className for Avatar container
 * @param fallbackClassName - Optional className for AvatarFallback
 * @param fallbackBgColor - Optional background color for fallback (default gradient)
 *
 * @example
 * ```tsx
 * <AvatarWithInitials
 *   name="John Doe"
 *   src="/avatar.jpg"
 *   className="h-16 w-16"
 * />
 * ```
 */
export const AvatarWithInitials = ({
  name,
  src,
  alt,
  className,
  fallbackClassName,
  fallbackBgColor,
}: AvatarWithInitialsProps) => {
  const initials = getInitials(name)

  return (
    <Avatar className={cn('h-10 w-10', className)}>
      {src && (
        <AvatarImage src={src} alt={alt ?? name} className="object-cover" />
      )}
      <AvatarFallback
        className={cn(
          'text-sm font-semibold',
          fallbackBgColor ??
            'bg-linear-to-br from-primary to-primary/80 text-primary-foreground',
          fallbackClassName,
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  )
}
